import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import IconButton from "@mui/material/IconButton";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionIcon from "@mui/icons-material/Description";
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";
import { Tooltip } from "@mui/material";

import cutStrAtAndAddDots from "utils/cutStrAtAndAddDots";
import { price2StringPrice } from "utils/priceformatter";
import {
  getParseStatusLabelById,
  getSystemStatusLabelById,
  getFileStatusLabelById,
} from "utils/labels";
import {
  getSystemStatusColorByLabel,
  getParseStatusColorByLabel,
  getFileStatusColorByLabel,
} from "utils/colors";

import CommonChip from "Components/UI/Tables/CommonChip";
import MUITable from "Components/UI/Tables/MUITable";
import RefreshBtn from "Components/UI/Tables/RefreshBtn";

import { getDebugFiles, updateEndCustomerPrinted } from "API/swesum";
import { usePopupNotificationContext } from "Context/NotificationPopup";

const createTableRows = (invoices) => {
  return invoices?.map((i) => {
    return [
      i.Id,
      price2StringPrice(i.Amount === null ? 0 : i.Amount),
      i.InvoiceId,
      i.FileId,
      i.OCR,
      i.EndCustomerName,
      i.LastPayment,

      getFileStatusLabelById(i.FileId),
      getParseStatusLabelById(i.ParseStatus),
      cutStrAtAndAddDots(i.ParseMessage, 45),
      getSystemStatusLabelById(i.SystemStatus),
      i.CreateTime,
      i.Id,
      { Id: i.Id, FileId: i.FileId },
    ];
  });
};

export default function EndCustomerPrintedTable({
  printedInvoices,
  fetchPrintedInvoices,
}) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    setRows(createTableRows(printedInvoices));
  }, [printedInvoices]);

  const { openSuccess, openError } = usePopupNotificationContext();

  const handleOnDebugClick = async (id) => {
    const res = await getDebugFiles(id);
    const blob = new Blob([res.data], { type: "application/zip" });
    const link = document.createElement("a");

    const url = window.URL.createObjectURL(blob);
    link.href = url;

    link.download = `debug_files_${id}.zip`;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleOnCancelPrintClick = async (id) => {
    const res = await updateEndCustomerPrinted(id, { FileId: -1 });

    if ((res.status = 200)) {
      openSuccess("Utskrift avbryten.");
      fetchPrintedInvoices();
    } else {
      openError("Något gick fel, kunde inte avbryta utskrift.");
      console.log(res);
    }
  };

  const columns = [
    {
      name: "",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex) => {
          return (
            <IconButton
              component={Link}
              to={`/endcustomerprinted/${rows[dataIndex][0]}`}
              aria-label="edit"
              size="small"
            >
              <LinkIcon sx={{ p: 0, m: 0 }} />
            </IconButton>
          );
        },
      },
    },
    {
      name: "ID",
      options: {
        filter: false,
      },
    },
    {
      name: "Belopp",
      options: {
        filter: false,
      },
    },
    {
      name: "Faktura ID",
      options: {
        // filter: false,
        empty: false,
        customBodyRenderLite: (dataIndex) => {
          return (
            <Link to={`/invoices/${rows[dataIndex][2]}`}>
              {rows[dataIndex][2]}
            </Link>
          );
        },
      },
    },
    {
      name: "Fil ID",
      options: {
        // filter: false,
        empty: false,
        customBodyRenderLite: (dataIndex) => {
          return (
            <Link to={`/files/${rows[dataIndex][3]}`}>
              {rows[dataIndex][3]}
            </Link>
          );
        },
      },
    },
    {
      name: "OCR",
      options: {
        filter: false,
      },
    },
    {
      name: "Namn",
      options: {
        filter: false,
      },
    },
    { name: "Senaste betalning" },
    {
      name: "Status",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getFileStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Kontrollstatus",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getParseStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Kontrollmeddelande",
      options: {
        filter: false,
      },
    },
    {
      name: "Status",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getSystemStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Skapad",
      options: {
        filter: false,
      },
    },
    {
      name: "Debug",
      options: {
        filter: false,
        customBodyRender: (value) => {
          return (
            <IconButton onClick={() => handleOnDebugClick(value)}>
              <DescriptionIcon />
            </IconButton>
          );
        },
      },
    },
    {
      name: "Utskrift",
      options: {
        filter: false,
        customBodyRender: (values) => {
          const isDisabled = values.FileId !== null;

          return (
            <Tooltip
              title={
                isDisabled ? (
                  <span style={{ whiteSpace: "pre-line" }}>
                    Avbryt utskick{"\n"}
                    (Åtgärd ej tillgänglig)
                  </span>
                ) : (
                  "Avbryt utskrift"
                )
              }
            >
              <span>
                <IconButton
                  onClick={() => handleOnCancelPrintClick(values.Id)}
                  disabled={isDisabled}
                >
                  <CancelScheduleSendIcon />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
    },
  ];

  const options = {
    customToolbar: () => {
      return (
        <>
          <RefreshBtn />
        </>
      );
    },
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    downloadOptions: {
      separator: ";",
      filename: "all_clients.csv",
    },
    rowsPerPage: 30,
    rowsPerPageOptions: [10, 30, 100],
    onDownload: (buildHead, buildBody, columns, data) => {
      return "\uFEFF" + buildHead(columns) + buildBody(data);
    },
  };

  return (
    <MUITable options={options} title={""} data={rows} columns={columns} />
  );
}
